import { useState } from "react";
import PublicShell, { Block, SampleNote } from "@/components/PublicShell";
import useSeo from "@/hooks/useSeo";

const STAGES = ["Brief", "Draft", "Your approval", "Scheduled"];

const SAMPLE_DRAFTS = [
  { channel: "Instagram", text: "Autumn openings this week — book a Saturday consultation before they go.", status: "Awaiting approval" },
  { channel: "Email", text: "Subject: Your seasonal check-in — quick openings this week", status: "Awaiting approval" },
  { channel: "Facebook", text: "New client slots released for November. First consultations are free this month.", status: "Awaiting approval" },
];

export default function AiMarketingPage() {
  useSeo("AI Marketing — Savy Kerno", "Savy Kerno drafts marketing strategies, posts, emails and ads for your review — AI-assisted marketing that fits your service business.");
  const [stage, setStage] = useState(0);
  return (
    <PublicShell
      testid="marketing-page"
      eyebrow="AI Marketing Studio"
      title="Campaigns drafted in seconds — sent only by you"
      sub="Describe the goal in plain words. The studio drafts strategy, posts, emails and ads for your review. Nothing publishes, sends or schedules without your explicit approval."
    >
      <Block title="The approval workflow" testid="marketing-workflow">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Workflow stages">
          {STAGES.map((s, i) => (
            <button
              key={s}
              role="tab"
              aria-selected={stage === i}
              data-testid={`marketing-stage-${i}`}
              onClick={() => setStage(i)}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${stage === i ? "bg-[#111111] text-white" : "border border-neutral-300 text-neutral-500 hover:border-neutral-400"}`}
            >
              {i + 1}. {s}
            </button>
          ))}
        </div>
        <div className="mt-4 rounded-2xl border border-neutral-200 bg-neutral-50/70 p-5 text-sm text-neutral-600" data-testid="marketing-stage-detail">
          {[
            "Tell the studio what you're promoting — an offer, a season, a quiet week you'd like to fill.",
            "AI drafts channel-ready copy: social posts, email campaigns and ad variants, matched to your business profile.",
            "You read, edit and approve each item. Rejected drafts go back for another pass — nothing leaves without you.",
            "Approved items schedule for their date. Publishing still needs the relevant account (like your Facebook Page) connected.",
          ][stage]}
        </div>
      </Block>

      <Block title="Example outputs" testid="marketing-samples">
        <div className="grid gap-3 sm:grid-cols-3">
          {SAMPLE_DRAFTS.map((d, i) => (
            <div key={i} className="rounded-2xl border border-neutral-200 p-5" data-testid={`marketing-sample-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-neutral-400">{d.channel}</span>
                <span className="rounded-full border border-neutral-300 px-2 py-0.5 text-[10px] font-semibold text-neutral-500">{d.status}</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-700">“{d.text}”</p>
              <p className="mt-3 text-[10px]"><SampleNote /></p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-neutral-500">
          Lead capture from Meta and Google ad platforms can feed your CRM directly once each provider is connected —
          each integration shows its real status in your dashboard.
        </p>
      </Block>

      <Block title="Guardrails, always on" testid="marketing-guardrails">
        <ul className="grid gap-3 text-sm text-neutral-600 sm:grid-cols-2">
          {[
            "Approval required before anything is published or sent",
            "Recipients limited to contacts with recorded consent",
            "Duplicates suppressed across imports and channels",
            "Every action attributed to your account — never shared across tenants",
          ].map((g) => (
            <li key={g} className="rounded-xl border border-neutral-200 px-4 py-3">{g}</li>
          ))}
        </ul>
      </Block>
    </PublicShell>
  );
}
